import { useState, type ChangeEvent, type FormEvent } from 'react'
import { CheckCircle2, Loader2, Send } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { getErrorMessage } from '@/utils/error'
import { useSubmitCompanyContact } from '@/services/contacts.service'

const EMPTY_FORM = { nome: '', empresa: '', email: '', telefone: '', website: '' }

/**
 * Formulário comercial da landing `/para-empresas#contato`.
 *
 * Fica fora de `ParaEmpresas.tsx` porque aquela página já passa de 900
 * linhas com animações de scroll — isolado aqui, ele é testável sem
 * montar a landing inteira.
 *
 * O campo `website` é honeypot: escondido do usuário, preenchido só por
 * bot. O back descarta o envio quando ele vem preenchido.
 */
const ContactForm = () => {
  const [form, setForm] = useState(EMPTY_FORM)
  const [sent, setSent] = useState(false)
  const { toast } = useToast()
  const submit = useSubmitCompanyContact()

  const handleChange = (field: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }))
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (submit.isPending) return

    try {
      await submit.mutateAsync({
        name: form.nome.trim(),
        company: form.empresa.trim(),
        email: form.email.trim(),
        phone: form.telefone.trim() || undefined,
        website: form.website,
      })
      setSent(true)
    } catch (error: unknown) {
      toast({
        title: 'Não foi possível enviar',
        description: getErrorMessage(error, 'Tente novamente em instantes.'),
        variant: 'destructive',
      })
    }
  }

  if (sent) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 text-center space-y-3">
        <CheckCircle2 className="w-12 h-12 text-primary mx-auto" />
        <h3 className="text-xl font-semibold text-foreground">Contato enviado</h3>
        <p className="text-muted-foreground text-sm">
          Recebemos seus dados. Nosso time comercial entra em contato pelo e-mail informado.
        </p>
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-card border border-border rounded-xl p-6 sm:p-8 space-y-5"
    >
      <div className="space-y-2">
        <Label htmlFor="nome">Nome</Label>
        <Input
          id="nome"
          value={form.nome}
          onChange={handleChange('nome')}
          placeholder="Seu nome completo"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="empresa">Empresa</Label>
        <Input
          id="empresa"
          value={form.empresa}
          onChange={handleChange('empresa')}
          placeholder="Nome da sua empresa"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">E-mail corporativo</Label>
        <Input
          id="email"
          type="email"
          value={form.email}
          onChange={handleChange('email')}
          placeholder="voce@empresa.com"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="telefone">Telefone</Label>
        <Input
          id="telefone"
          type="tel"
          value={form.telefone}
          onChange={handleChange('telefone')}
          placeholder="(11) 99999-9999"
        />
      </div>

      {/* Honeypot — invisível e fora da ordem de tabulação. */}
      <div className="hidden" aria-hidden="true">
        <Label htmlFor="website">Não preencha este campo</Label>
        <Input
          id="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={handleChange('website')}
        />
      </div>

      <button
        type="submit"
        disabled={submit.isPending}
        className="btn-primary w-full flex items-center justify-center gap-2 text-sm py-3 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submit.isPending ? (
          <>
            Enviando <Loader2 className="w-4 h-4 animate-spin" />
          </>
        ) : (
          <>
            Enviar <Send className="w-4 h-4" />
          </>
        )}
      </button>
    </form>
  )
}

export default ContactForm
